import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Carousel, CarouselItem, Col, Row } from 'reactstrap';
import { compact, range } from 'underscore';
import cx from 'classnames';
import MediaQuery from 'react-responsive';
import MaterialIcon from 'material-icons-react';

import CarouselControl from './carousel-control';

import BS from '../../theme/Bootstrap';
import { ucFirst } from '../../utilities';

const BREAKPOINTS = {
  print: undefined,
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};

class ResponsiveCarousel extends PureComponent {
  static contextTypes = {
    theme: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.state = {
      printActiveIndex: props.printActiveIndex || props.activeIndex || 0,
      xsActiveIndex: props.xsActiveIndex || props.activeIndex || 0,
      smActiveIndex: props.smActiveIndex || props.activeIndex * props.smNumPerPage || 0,
      mdActiveIndex: props.mdActiveIndex || props.activeIndex * props.mdNumPerPage || 0,
      lgActiveIndex: props.lgActiveIndex || props.activeIndex * props.lgNumPerPage || 0,
      xlActiveIndex: props.xlActiveIndex || props.activeIndex * props.xlNumPerPage || 0
    };

    this.animating = 0;
  }

  render() {
    const carousels = this.generateCarousels();
    const BS = this.context.theme;

    return carousels.map(
      (
        { breakpoint, breakpoints, activeIndex, className, numPages, minWidth, maxWidth },
        i
      ) => {
        const carousel = carousels[i];

        if (breakpoint === 'print') {
          return (
            <Carousel
              key={i}
              activeIndex={activeIndex}
              className={cx(
                this.props.className,
                BS.mb3,
                className,
                BS.dNone,
                BS.dPrintBlock
              )}
              next={() => {}}
              previous={() => {}}
              keyboard={false}
              interval={false}
            >
              {this.renderCarouselItems({
                ...carousel,
                numPages: numPages > 1 ? 1 : numPages
              })}
            </Carousel>
          );
        }

        return (
          <MediaQuery key={i} minWidth={minWidth} maxWidth={maxWidth}>
            <Carousel
              activeIndex={activeIndex}
              className={cx(this.props.className, BS.mb3, className, {
                [BS.dPrintNone]: breakpoints.indexOf('print') > -1
              })}
              next={() => this.next(carousel)}
              previous={() => this.prev(carousel)}
              keyboard={this.props.keyboard}
              interval={10000}
            >
              {this.renderCarouselItems(carousel)}
              {numPages > 1 && (
                <CarouselControl
                  direction="prev"
                  directionText="Previous"
                  className={BS.carouselControlPrev}
                  onClickHandler={() => this.prev(carousel)}
                >
                  <MaterialIcon icon="navigate_before" size="large" color="inherit" />
                </CarouselControl>
              )}
              {numPages > 1 && (
                <CarouselControl
                  direction="next"
                  directionText="Next"
                  className={BS.carouselControlNext}
                  onClickHandler={() => this.next(carousel)}
                >
                  <MaterialIcon icon="navigate_next" size="large" color="inherit" />
                </CarouselControl>
              )}
            </Carousel>
          </MediaQuery>
        );
      }
    );
  }

  renderCarouselItems({ numPerPage, numPages, groupClassName }) {
    const BS = this.context.theme;
    const { children, placeholderImg } = this.props;

    if (numPages > 0) {
      return range(numPages).map((page, i) => {
        return (
          <CarouselItem
            key={i}
            onEntered={node => {
              this.animating = this.animating > 0 ? this.animating - 1 : 0;
            }}
            onExit={node => {
              this.animating = this.animating + 1;
            }}
          >
            <Row className={cx(BS.gutters1)}>
              {range(numPerPage).map(k => {
                const child = children[i * numPerPage + k];

                return (
                  <Col key={k} className={cx(BS.mb1, groupClassName, BS.textCenter)}>
                    {child || (
                      <img
                        src={placeholderImg}
                        alt="Not Found"
                        className={cx(BS.imgFluid, BS.w100)}
                      />
                    )}
                  </Col>
                );
              })}
            </Row>
          </CarouselItem>
        );
      });
    } else {
      return [
        <CarouselItem key={0}>
          <Row className={cx(BS.gutters1)}>
            {range(numPerPage).map(i => (
              <Col key={i} className={cx(BS.mb1, groupClassName)}>
                <img
                  src={placeholderImg}
                  alt="Not Found"
                  className={cx(BS.imgFluid, BS.w100)}
                />
              </Col>
            ))}
          </Row>
        </CarouselItem>
      ];
    }
  }

  next(carousel) {
    const { indexKey } = carousel;
    const newIndex = this.state[indexKey] + 1;

    this.goto(carousel, newIndex);
  }

  prev(carousel) {
    const { indexKey } = carousel;
    const newIndex = this.state[indexKey] - 1;

    this.goto(carousel, newIndex);
  }

  goto({ numPerPage, activeIndex, indexKey, numPages }, newIndex) {
    const { paused } = this.props;

    if (newIndex >= numPages) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = numPages - 1;
    }

    if (this.animating > 0 || paused) {
      return;
    }

    this.setState({
      [indexKey]: newIndex
    });
  }

  generateCarousels() {
    // determine configured breakpoints
    const breakpoints = compact(
      ['print', 'xs', 'sm', 'md', 'lg', 'xl'].map(breakpoint =>
        this.props[`${breakpoint}NumPerPage`] ? breakpoint : null
      )
    );

    // build carousels for each configured breakpoint
    return breakpoints.map((breakpoint, i) => {
      const nextBreak = breakpoints[i + 1];

      let classNames = [];

      if (nextBreak) {
        classNames.push(BS[`d${ucFirst(nextBreak)}None`]);
      }

      return {
        breakpoint: breakpoint,
        breakpoints: breakpoints,
        numPerPage: this.props[`${breakpoint}NumPerPage`],
        activeIndex: this.state[`${breakpoint}ActiveIndex`],
        indexKey: `${breakpoint}ActiveIndex`,
        className: classNames.join(' '),
        groupClassName: cx(
          this.props.itemClassName,
          this.props[`${breakpoint}ClassName`]
        ),
        numPages: Math.ceil(
          this.props.children.length / this.props[`${breakpoint}NumPerPage`]
        ),
        minWidth: BREAKPOINTS[breakpoint],
        maxWidth: nextBreak ? BREAKPOINTS[nextBreak] : undefined
      };
    });
  }
}

ResponsiveCarousel.defaultProps = {
  items: [],
  activeIndex: 0,
  printActiveIndex: 0,
  xsActiveIndex: 0,
  smActiveIndex: 0,
  mdActiveIndex: 0,
  lgActiveIndex: 0,
  xlActiveIndex: 0,
  printNumPerPage: undefined,
  xsNumPerPage: 1,
  smNumPerPage: undefined,
  mdNumPerPage: undefined,
  lgNumPerPage: undefined,
  xlNumPerPage: undefined,
  printClassName: undefined,
  xsClassName: undefined,
  smClassName: undefined,
  mdClassName: undefined,
  lgClassName: undefined,
  xlClassName: undefined
};

export default ResponsiveCarousel;



// WEBPACK FOOTER //
// ./src/components/carousel/carousel.js